import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import Switch from '../../../../components/Switch';
import useDebounce from '../../../../hooks/useDebounce';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { getStartEndOfMonth } from '../../../../utils/methods';
import { AuditLogListFilters } from '../../../../interfaces/auditLog.interface';

interface FiltersProps {
  entities: string[];
  modules: { code: string; name: string }[];
  events: string[];
  users: { id: number; name: string }[];
  initialFilters: AuditLogListFilters;
  onApply: (filters: AuditLogListFilters) => void;
  onReset: () => void;
}

const Filters: React.FC<FiltersProps> = ({
  entities, modules, events, users, initialFilters, onApply, onReset,
}) => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AuditLogListFilters>(initialFilters);
  const debouncedSearchText = useDebounce(filters.searchText, 500);
  const defaultDates = useMemo(() => getStartEndOfMonth(), []);

  // A filter is "applied" when it differs from the page defaults (current month, nothing else set)
  const isApplied = useCallback((f: AuditLogListFilters) => (
    f.searchText !== '' || f.entity !== '' || f.module !== '' || f.event !== '' || f.user !== '' || Boolean(f.systemOnly)
    || f.startDate !== defaultDates.startDate || f.endDate !== defaultDates.endDate
  ), [defaultDates]);

  const [filtersApplied, setFiltersApplied] = useState(isApplied(initialFilters));

  useEffect(() => {
    setFilters(initialFilters);
  }, [initialFilters]);

  // Apply every change straight away; the description text waits for a short pause in typing
  useEffect(() => {
    const next: AuditLogListFilters = {
      searchText: debouncedSearchText,
      entity: filters.entity,
      module: filters.module,
      event: filters.event,
      user: filters.user,
      systemOnly: Boolean(filters.systemOnly),
      startDate: filters.startDate,
      endDate: filters.endDate,
    };
    setFiltersApplied(isApplied(next));
    onApply(next);
  }, [debouncedSearchText, filters.entity, filters.module, filters.event, filters.user, filters.systemOnly, filters.startDate, filters.endDate, isApplied, onApply]);

  const handleInputChange = useCallback((event: any, name: string) => {
    const value = event.target.value;
    setFilters((prev) => {
      const next = { ...prev, [name]: value };
      // A chosen user and "System activity only" exclude each other
      if (name === 'user' && value) {
        next.systemOnly = false;
      }
      // The from date can never be after the to date: move the other end along
      if (name === VALIDATION_FIELD_MAPPING.startDate && value && next.endDate && value > next.endDate) {
        next.endDate = value;
      }
      if (name === VALIDATION_FIELD_MAPPING.endDate && value && next.startDate && value < next.startDate) {
        next.startDate = value;
      }
      return next;
    });
  }, []);

  const handleReset = () => {
    setFilters({ searchText: '', entity: '', module: '', event: '', user: '', systemOnly: false, ...defaultDates });
    onReset();
  };

  // "Clear filters" is small text like on the other list pages; it can also be used from the keyboard
  const handleResetKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleReset();
    }
  };

  const handleSystemOnlyChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setFilters((prev) => ({ ...prev, systemOnly: checked, user: checked ? '' : prev.user }));
  }, []);

  const toOptions = (items: string[]) => (items || []).map((item) => ({ label: item, value: item }));
  const moduleOptions = (modules || []).map((module) => ({ label: module.name, value: module.code }));
  const userOptions = (users || []).map((user) => ({ label: user.name, value: String(user.id) }));

  return (
    <div className={`card mb-4 ${filtersApplied ? 'border border-primary border-2' : ''}`} data-testid="audit-log-filters">
      <div className="card-body">
        <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
          <h5 className="m-0 text-dark">{t('auditLogs.list.pageTexts.filters.title')}</h5>
          {filtersApplied && (
            <small
              role="button"
              tabIndex={0}
              onClick={handleReset}
              onKeyDown={handleResetKeyDown}
              className="cursor-pointer text-primary"
            >
              {t('auditLogs.list.pageTexts.filters.clear')}
            </small>
          )}
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-2 col-sm-4">
            <div className="form-floating form-floating-outline">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.startDate}
                name={VALIDATION_FIELD_MAPPING.startDate}
                label={t('auditLogs.list.pageTexts.filters.startDate')}
                placeholder={t('auditLogs.list.pageTexts.filters.startDate')}
                max={filters.endDate || undefined}
                value={filters.startDate}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="col-md-2 col-sm-4">
            <div className="form-floating form-floating-outline">
              <Input
                type="date"
                id={VALIDATION_FIELD_MAPPING.endDate}
                name={VALIDATION_FIELD_MAPPING.endDate}
                label={t('auditLogs.list.pageTexts.filters.endDate')}
                placeholder={t('auditLogs.list.pageTexts.filters.endDate')}
                min={filters.startDate || undefined}
                value={filters.endDate}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="col-md-2 col-sm-4">
            <Dropdown
              values={filters.entity ? [filters.entity] : []}
              options={toOptions(entities)}
              allOptions={toOptions(entities)}
              id={VALIDATION_FIELD_MAPPING.auditEntity}
              name="entity"
              noSearch
              placeholder={t('auditLogs.list.pageTexts.filters.selectEntity')}
              label={t('auditLogs.list.pageTexts.filters.entity')}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-2 col-sm-4">
            <Dropdown
              values={filters.module ? [filters.module] : []}
              options={moduleOptions}
              allOptions={moduleOptions}
              id={VALIDATION_FIELD_MAPPING.auditModule}
              name="module"
              noSearch
              placeholder={t('auditLogs.list.pageTexts.filters.selectModule')}
              label={t('auditLogs.list.pageTexts.filters.module')}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-2 col-sm-4">
            <Dropdown
              values={filters.event ? [filters.event] : []}
              options={toOptions(events)}
              allOptions={toOptions(events)}
              id={VALIDATION_FIELD_MAPPING.auditEvent}
              name="event"
              placeholder={t('auditLogs.list.pageTexts.filters.selectEvent')}
              label={t('auditLogs.list.pageTexts.filters.event')}
              onChange={handleInputChange}
            />
          </div>
          <div className="col-md-2 col-sm-4">
            <Dropdown
              values={filters.user ? [filters.user] : []}
              options={userOptions}
              allOptions={userOptions}
              id={VALIDATION_FIELD_MAPPING.auditUser}
              name="user"
              placeholder={t('auditLogs.list.pageTexts.filters.selectUser')}
              label={t('auditLogs.list.pageTexts.filters.user')}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-4 col-sm-12">
            <div className="form-floating form-floating-outline">
              <Input
                type="search"
                id={VALIDATION_FIELD_MAPPING.searchText}
                name="searchText"
                label={t('auditLogs.list.pageTexts.filters.search')}
                placeholder={t('auditLogs.list.pageTexts.filters.searchPlaceholder')}
                value={filters.searchText}
                onChange={handleInputChange}
                autoComplete="on"
              />
            </div>
          </div>
          <div className="col-md-3 col-sm-12 d-flex align-items-center">
            <Switch
              name="systemOnly"
              label={t('auditLogs.list.pageTexts.filters.systemOnly')}
              checked={Boolean(filters.systemOnly)}
              onChange={handleSystemOnlyChange}
              offColorGrey
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filters;
